import { memo } from 'react';
import { useTranslation } from 'react-i18next';
import { Select } from '@components/shared/forms/Dropdowns';
import { useFlags } from 'launchdarkly-react-client-sdk';
import Input from './Input';
import YesNoMaybe from './YesNoMaybe';
import css from './styles.module.less';

type CountRowProps = {
  labelKey: string;
  value: number;
};

const CountWithSource = memo(({ labelKey, value }: CountRowProps) => {
  const { t } = useTranslation('admin');
  const showSource = value || value === 0;
  return (
    <fieldset className={css.sideBySide}>
      <Input
        key={`building.${labelKey}`}
        containerClass={showSource ? css.left : css.fullWidth}
        labelText={t(`building.${labelKey}`)}
        name={`metadata.${labelKey}`}
        type="number"
      />
      {showSource ? (
        <Input
          key={`building.${labelKey}DataSource`}
          containerClass={css.right}
          labelText={t('building.dataSource')}
          name={`metadata.${labelKey}DataSource`}
        />
      ) : null}
    </fieldset>
  );
});

type RowProps = {
  labelKey: string;
  value: string;
};

const YesNoMaybeWithSource = memo(({ labelKey, value }: RowProps) => {
  const { t } = useTranslation('admin');

  return (
    <fieldset className={css.sideBySide}>
      <YesNoMaybe
        key={`building.${labelKey}`}
        containerClass={value ? css.left : css.fullWidth}
        labelText={t(`building.${labelKey}`)}
        name={`metadata.${labelKey}`}
        value={value}
      />
      {value ? (
        <Input
          key={`building.${labelKey}DataSource`}
          containerClass={css.right}
          labelText={t('building.dataSource')}
          name={`metadata.${labelKey}DataSource`}
        />
      ) : null}
    </fieldset>
  );
});

const ElevatorPosition = memo(({ value }: { value: string }) => {
  const { t } = useTranslation('admin');

  return (
    <fieldset className={css.sideBySide}>
      <Select
        key="building.passengerElevatorPosition"
        className={css.select}
        containerClass={value ? css.left : css.fullWidth}
        isClearable
        labelText={t(`building.passengerElevatorPosition.label`)}
        name="metadata.passengerElevatorPosition"
        options={[
          {
            label: t('building.passengerElevatorPosition.core'),
            value: 'core',
          },
          {
            label: t('building.passengerElevatorPosition.side'),
            value: 'side',
          },
        ]}
        placeholder=""
        value={value}
      />
      {value ? (
        <Input
          key="building.passengerElevatorPositionDataSource"
          containerClass={css.right}
          labelText={t('building.dataSource')}
          name="metadata.passengerElevatorPositionDataSource"
        />
      ) : null}
    </fieldset>
  );
});

type Props = {
  entranceCount: number;
  passengerElevatorCount: number;
  serviceElevatorCount: number;
  passengerElevatorPosition: string;
  twentyFourSevenAccess: string;
  barAvailable: string;
  bikeStorageAvailable: string;
  cafeAvailable: string;
  commonAreaWifi: string;
  communalLobbySpace: string;
  communalOutdoorSpace: string;
  communalRoofAccess: string;
  flexSpaceAvailable: string;
  foodServiceAvailable: string;
  generalStoreAvailable: string;
  gymAvailable: string;
  keyCardBuildingEntry: string;
  landmarkBuilding: string;
  lobbyAttendant: string;
  lobbyBeverageAvailable: string;
  lobbyFoodAvailable: string;
  messengerCenter: string;
  mobileConnectivityImprovements: string;
  optionToAddInternetServiceProvider: string;
  parkingAvailable: string;
  petsAllowed: string;
  pharmacyAvailable: string;
  propertyManagerOnSite: string;
  restaurantAvailable: string;
  spaAvailable: string;
  storageAvailable: string;
  turnstiles: string;
  urgentCareDoctorOnsite: string;
  yogaStudioAvailable: string;
};

/* eslint-disable react/destructuring-assignment */
const Amenities = (props: Props) => {
  const { t } = useTranslation('admin');
  const flags = useFlags();
  return (
    <section className={css.amenities}>
      <h2>{t('building.amenities.title')}</h2>
      {flags['amenities-data-tabs'] ? null : (
        <CountWithSource labelKey="entranceCount" key="entranceCount" value={props.entranceCount} />
      )}
      <CountWithSource
        labelKey="passengerElevatorCount"
        key="passengerElevatorCount"
        value={props.passengerElevatorCount}
      />
      <CountWithSource
        labelKey="serviceElevatorCount"
        key="serviceElevatorCount"
        value={props.serviceElevatorCount}
      />
      {flags['amenities-data-tabs'] ? null : (
        <ElevatorPosition value={props.passengerElevatorPosition} />
      )}
      <YesNoMaybeWithSource
        labelKey="twentyFourSevenAccess"
        key="twentyFourSevenAccess"
        value={props.twentyFourSevenAccess}
      />
      <YesNoMaybeWithSource labelKey="barAvailable" key="barAvailable" value={props.barAvailable} />
      <YesNoMaybeWithSource
        labelKey="bikeStorageAvailable"
        key="bikeStorageAvailable"
        value={props.bikeStorageAvailable}
      />
      <YesNoMaybeWithSource
        labelKey="cafeAvailable"
        key="cafeAvailable"
        value={props.cafeAvailable}
      />
      <YesNoMaybeWithSource
        labelKey="commonAreaWifi"
        key="commonAreaWifi"
        value={props.commonAreaWifi}
      />
      <YesNoMaybeWithSource
        labelKey="communalLobbySpace"
        key="communalLobbySpace"
        value={props.communalLobbySpace}
      />
      <YesNoMaybeWithSource
        labelKey="communalOutdoorSpace"
        key="communalOutdoorSpace"
        value={props.communalOutdoorSpace}
      />
      <YesNoMaybeWithSource
        labelKey="communalRoofAccess"
        key="communalRoofAccess"
        value={props.communalRoofAccess}
      />
      <YesNoMaybeWithSource
        labelKey="flexSpaceAvailable"
        key="flexSpaceAvailable"
        value={props.flexSpaceAvailable}
      />
      <YesNoMaybeWithSource
        labelKey="foodServiceAvailable"
        key="foodServiceAvailable"
        value={props.foodServiceAvailable}
      />
      <YesNoMaybeWithSource
        labelKey="generalStoreAvailable"
        key="generalStoreAvailable"
        value={props.generalStoreAvailable}
      />
      <YesNoMaybeWithSource labelKey="gymAvailable" key="gymAvailable" value={props.gymAvailable} />
      <YesNoMaybeWithSource
        labelKey="keyCardBuildingEntry"
        key="keyCardBuildingEntry"
        value={props.keyCardBuildingEntry}
      />
      {flags['amenities-data-tabs'] ? null : (
        <YesNoMaybeWithSource
          labelKey="landmarkBuilding"
          key="landmarkBuilding"
          value={props.landmarkBuilding}
        />
      )}
      <YesNoMaybeWithSource
        labelKey="lobbyAttendant"
        key="lobbyAttendant"
        value={props.lobbyAttendant}
      />
      <YesNoMaybeWithSource
        labelKey="lobbyBeverageAvailable"
        key="lobbyBeverageAvailable"
        value={props.lobbyBeverageAvailable}
      />
      <YesNoMaybeWithSource
        labelKey="lobbyFoodAvailable"
        key="lobbyFoodAvailable"
        value={props.lobbyFoodAvailable}
      />
      <YesNoMaybeWithSource
        labelKey="messengerCenter"
        key="messengerCenter"
        value={props.messengerCenter}
      />
      {flags['amenities-data-tabs'] ? null : (
        <YesNoMaybeWithSource
          labelKey="mobileConnectivityImprovements"
          key="mobileConnectivityImprovements"
          value={props.mobileConnectivityImprovements}
        />
      )}
      <YesNoMaybeWithSource
        labelKey="optionToAddInternetServiceProvider"
        key="optionToAddInternetServiceProvider"
        value={props.optionToAddInternetServiceProvider}
      />
      <YesNoMaybeWithSource
        labelKey="parkingAvailable"
        key="parkingAvailable"
        value={props.parkingAvailable}
      />
      <YesNoMaybeWithSource labelKey="petsAllowed" key="petsAllowed" value={props.petsAllowed} />
      <YesNoMaybeWithSource
        labelKey="pharmacyAvailable"
        key="pharmacyAvailable"
        value={props.pharmacyAvailable}
      />
      <YesNoMaybeWithSource
        labelKey="propertyManagerOnSite"
        key="propertyManagerOnSite"
        value={props.propertyManagerOnSite}
      />
      <YesNoMaybeWithSource
        labelKey="restaurantAvailable"
        key="restaurantAvailable"
        value={props.restaurantAvailable}
      />
      <YesNoMaybeWithSource labelKey="spaAvailable" key="spaAvailable" value={props.spaAvailable} />
      <YesNoMaybeWithSource
        labelKey="storageAvailable"
        key="storageAvailable"
        value={props.storageAvailable}
      />
      <YesNoMaybeWithSource labelKey="turnstiles" key="turnstiles" value={props.turnstiles} />
      <YesNoMaybeWithSource
        labelKey="urgentCareDoctorOnsite"
        key="urgentCareDoctorOnsite"
        value={props.urgentCareDoctorOnsite}
      />
      <YesNoMaybeWithSource
        labelKey="yogaStudioAvailable"
        key="yogaStudioAvailable"
        value={props.yogaStudioAvailable}
      />
    </section>
  );
};

export default memo(Amenities);
